import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './components/App/App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import ScrollToTop from './assets/hooks/scrollToTop';
import { OAUTH_GOOGLE_CLIENT_ID } from './assets/utils/constants';
import * as Sentry from "@sentry/browser";

Sentry.init({ dsn: "https://02a0420823204b35b9612f90c9bc30db@sentry.cabahub.com/1", environment: "production", });

// Wait for fonts to load
const Root = () => {
  const [isFontsLoaded, setFontsLoaded] = useState(false);
  const [, setForceUpdate] = useState(false);

  async function preloadUsedFonts() {
    console.log('🚀 Font Preloading Started');

    // Step 1: Wait until fonts are registered
    await document.fonts.ready;
    console.log('✅ document.fonts.ready');

    const fontFamilies = new Set();
    // Use predefined fonts
    fontFamilies.add('Inter');
    fontFamilies.add('Oswald');

    // // Step 2: Extract used fonts from computed styles
    // document.querySelectorAll("*").forEach(el => {
    //   console.log(getComputedStyle(el).fontFamily)
    //   const font = getComputedStyle(el).fontFamily.split(",")[0].replace(/['"]/g, "").trim();
    //   if (font) fontFamilies.add(font);
    // });

    console.log('🔍 Used Fonts:', Array.from(fontFamilies));
    // Step 3: Find font URLs in stylesheets
    const fontUrls = new Map();
    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        Array.from(sheet.cssRules || []).forEach((rule) => {
          if (rule instanceof CSSFontFaceRule) {
            const fontName = rule.style.fontFamily.replace(/['"]/g, '').trim();
            const fontWeight = rule.style.fontWeight || '400';
            const fontStyle = rule.style.fontStyle || 'normal';
            const fontUrlMatches = [
              ...rule.style.src.matchAll(/url\(["']?([^"')]+)["']?\)/g),
            ].map((match) => match[1]);

            if (fontUrlMatches) {
              const fontUrlList = fontUrlMatches.map((match) =>
                match.replace(/url\(["']?|["']?\)/g, '')
              ); // Clean URLs

              // Find the best format for the browser
              const preferredFormat =
                fontUrlList.find((url) => url.includes('.woff2')) ||
                fontUrlList.find((url) => url.includes('.woff')) ||
                fontUrlList.find((url) => url.includes('.ttf')) ||
                fontUrlList.find((url) => url.includes('.otf')) ||
                fontUrlList.find((url) => url.includes('.eot')); // Only use EOT as a last resort

              if (preferredFormat) {
                const fontKey = `${fontName}-${fontWeight}-${fontStyle}`;
                if (!fontUrls.get(fontKey)) {
                  fontUrls.set(fontKey, [preferredFormat]);
                } else {
                  fontUrls.set(
                    fontKey,
                    fontUrls.get(fontKey).concat([preferredFormat])
                  );
                }
              }
            }
          }
        });
      } catch (err) {
        console.warn('⚠️ Skipping stylesheet due to CORS', err);
      }
    });
    console.log('🔗 Font URLs:', fontUrls);

    const fontExtensions = {
      '.woff2': 'woff2',
      '.woff': 'woff',
      '.ttf': 'truetype',
      '.otf': 'opentype',
      '.eot': 'embedded-opentype',
    };

    const getFontType = (fontUrl) => {
      const ext = Object.keys(fontExtensions).find((ext) =>
        fontUrl.includes(ext)
      );
      return ext ? fontExtensions[ext] : 'woff'; // Default to WOFF if unknown
    };

    // Step 4: Preload fonts that are actually used
    const fontPromises = Array.from(fontFamilies).flatMap((font) => {
      const fontVariants = Array.from(fontUrls.keys()).filter(key => key.startsWith(font));
      if (!fontVariants.length) return [];

      return fontVariants.map((fontKey) => {
        const [fontName, fontWeight, fontStyle] = fontKey.split('-');
        const localFontUrls = fontUrls.get(fontKey);
        if (!localFontUrls) return [];

        return localFontUrls.map((fontUrl) => {
          let fontType = getFontType(fontUrl);
          console.log(`⏳ Loading: ${fontName} (${fontWeight}, ${fontStyle}) from ${fontUrl}`);

          const fontFace = new FontFace(
            fontName,
            `url(${fontUrl}) format('${fontType}')`,
            {
              weight: fontWeight,
              style: fontStyle
            }
          );

          return fontFace
            .load()
            .then((loadedFont) => {
              document.fonts.add(loadedFont);
              console.log(`✅ Loaded: ${fontName} (${fontWeight}, ${fontStyle})`);
            })
            .catch((err) => {
              console.warn(`❌ Failed to load: ${fontName} (${fontWeight}, ${fontStyle}), ${err}`);
              return null;
            });
        });
      }).flat();
    });

    // Step 5: Ensure all fonts are completely loaded before applying
    await Promise.all(fontPromises)
      .then(() => {
        console.log('🎉 All fonts are now fully loaded!');
        document.documentElement.classList.add('fonts-loaded'); // Apply class when ready
      })
      .catch(() => {
        console.warn('⚠️ Some fonts failed, but continuing...');
        document.documentElement.classList.add('fonts-loaded'); // Apply class when ready
      });
  }

  useEffect(() => {
    preloadUsedFonts();

    const checkFonts = () =>
      document.documentElement.classList.contains('fonts-loaded');

    if (checkFonts()) {
      setFontsLoaded(true);
    } else {
      const interval = setInterval(() => {
        if (checkFonts()) {
          clearInterval(interval);
          setFontsLoaded(true);

          setTimeout(() => {
            setForceUpdate((prev) => !prev); // React re-render
            document.body.style.display = 'none'; // Force repaint
            requestAnimationFrame(() => (document.body.style.display = ''));
          }, 50);
        }
      }, 100);
    }
  }, []);

  return isFontsLoaded ? <App /> : null;
};


const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <GoogleOAuthProvider clientId={OAUTH_GOOGLE_CLIENT_ID}>
    <React.StrictMode>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ScrollToTop />
        <Root />
      </BrowserRouter>
    </React.StrictMode>
  </GoogleOAuthProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
